import { http } from './client';

/** Config schema field from Fleet API (mirror of C# ConfigFieldDto). */
export interface ConfigSchemaField {
    key: string;
    type: 'string' | 'secret' | 'integer' | 'boolean' | 'enum';
    required: boolean;
    default?: string | number | boolean | null;
    description?: string;
    options?: string[];
}

/** Config schema wrapper. */
export interface ConfigSchema {
    fields: ConfigSchemaField[];
}

/** Module catalog entry from Fleet API. */
export interface ModuleCatalogEntry {
    moduleId: string;
    version: string;
    displayName: string;
    description: string;
    category: string;
    icon: string;
    requiredModules: string[];
    transportProtocols: string[];
    ports: { port: number; protocol: string }[];
    minNodeVersion?: string;
    configSchema?: ConfigSchema | null;
}

/** Module installation on a node. */
export interface ModuleInstallation {
    id: string;
    nodeId: string;
    moduleId: string;
    version: string;
    desiredState: string;
    actualStatus: string;
    desiredRevision: number;
    createdAt: string;
    updatedAt: string;
    config?: Record<string, string> | null;
}

/** Fleet node. */
export interface FleetNode {
    id: string;
    name: string;
    hostname: string;
    role: string;
    status: string;
    agentPort?: number;
    transportType: string;
    tunnelIp?: string;
    publicKey?: string;
    upstreamIp?: string;
    publicIp?: string;
    routedCidrs: string[];
    agentVersion?: string;
    factsJson?: string;
    lastSeenAt?: string;
    createdAt: string;
    modules: ModuleInstallation[];
}

/** Parsed hardware metrics from NodeAgent heartbeat (FactsJson). */
export interface NodeFacts {
    hostname?: string;
    os?: string;
    arch?: string;
    kernel?: string;
    dockerAvailable: boolean;
    agentVersion?: string;
    systemMetrics?: {
        cpuUsagePercent: number;
        memoryTotalBytes: number;
        memoryUsedBytes: number;
        diskTotalBytes: number;
        diskUsedBytes: number;
        networkRxBytes: number;
        networkTxBytes: number;
        cpuCores: number;
        loadAverage: number;
    };
}

/** Parse FactsJson string into NodeFacts object.
 *  FactsJson is serialized by Controller with System.Text.Json (PascalCase),
 *  so we need to map PascalCase properties to camelCase. */
export function parseFacts(factsJson?: string): NodeFacts | null {
    if (!factsJson) return null;
    try {
        const raw = JSON.parse(factsJson) as Record<string, unknown>;
        const sm = raw['SystemMetrics'] as Record<string, number> | undefined;
        return {
            hostname: raw['Hostname'] as string | undefined,
            os: raw['Os'] as string | undefined,
            arch: raw['Arch'] as string | undefined,
            kernel: raw['Kernel'] as string | undefined,
            dockerAvailable: raw['DockerAvailable'] as boolean | undefined ?? false,
            agentVersion: raw['AgentVersion'] as string | undefined,
            systemMetrics: sm ? {
                cpuUsagePercent: sm['CpuUsagePercent'] ?? 0,
                memoryTotalBytes: sm['MemoryTotalBytes'] ?? 0,
                memoryUsedBytes: sm['MemoryUsedBytes'] ?? 0,
                diskTotalBytes: sm['DiskTotalBytes'] ?? 0,
                diskUsedBytes: sm['DiskUsedBytes'] ?? 0,
                networkRxBytes: sm['NetworkRxBytes'] ?? 0,
                networkTxBytes: sm['NetworkTxBytes'] ?? 0,
                cpuCores: sm['CpuCores'] ?? 0,
                loadAverage: sm['LoadAverage'] ?? 0,
            } : undefined,
        };
    } catch {
        return null;
    }
}

export interface InstallModuleRequest {
    moduleId: string;
    version: string;
    config?: Record<string, string>;
}

export interface UpdateModuleRequest {
    version?: string;
    config?: Record<string, string>;
    desiredState?: string;
}

export interface MeshLink {
    id: string;
    fromNodeId: string;
    toNodeId: string;
    transportProtocol: string;
    status: string;
    config?: Record<string, string>;
    createdAt: string;
    updatedAt: string;
}

export const fleetApi = {
    // --- Nodes ---
    async listNodes(): Promise<FleetNode[]> {
        const { data } = await http.get<FleetNode[]>('/v1/fleet/nodes');
        return data;
    },

    async getNode(id: string): Promise<FleetNode> {
        const { data } = await http.get<FleetNode>(`/v1/fleet/nodes/${id}`);
        return data;
    },

    async createNode(req: {
        name: string;
        hostname: string;
        role: string;
        agentPort?: number;
        transportType?: string;
    }): Promise<FleetNode> {
        const { data } = await http.post<FleetNode>('/v1/fleet/nodes', req);
        return data;
    },

    // --- Module Catalog ---
    async listCatalog(): Promise<ModuleCatalogEntry[]> {
        const { data } = await http.get<ModuleCatalogEntry[]>('/v1/fleet/modules');
        return data;
    },

    async addFromGit(gitUrl: string, branch?: string): Promise<ModuleCatalogEntry> {
        const { data } = await http.post<ModuleCatalogEntry>('/v1/fleet/modules/from-git', {
            gitUrl,
            branch
        });
        return data;
    },

    async refreshModule(moduleId: string, gitUrl: string, branch?: string): Promise<ModuleCatalogEntry> {
        const { data } = await http.post<ModuleCatalogEntry>(
            `/v1/fleet/modules/${moduleId}/refresh`,
            { gitUrl, branch }
        );
        return data;
    },

    async removeModule(moduleId: string, version: string): Promise<void> {
        await http.delete(`/v1/fleet/modules/${moduleId}/${version}`);
    },

    // --- Module Install ---
    async listInstalled(nodeId: string): Promise<ModuleInstallation[]> {
        const { data } = await http.get<ModuleInstallation[]>(`/v1/fleet/nodes/${nodeId}/modules`);
        return data;
    },

    async getInstalledModule(nodeId: string, moduleId: string): Promise<ModuleInstallation> {
        const { data } = await http.get<ModuleInstallation>(`/v1/fleet/nodes/${nodeId}/modules/${moduleId}`);
        return data;
    },

    async installModule(nodeId: string, req: InstallModuleRequest): Promise<ModuleInstallation> {
        const { data } = await http.post<ModuleInstallation>(
            `/v1/fleet/nodes/${nodeId}/modules`,
            req
        );
        return data;
    },

    async updateModule(nodeId: string, moduleId: string, req: UpdateModuleRequest): Promise<ModuleInstallation> {
        const { data } = await http.put<ModuleInstallation>(
            `/v1/fleet/nodes/${nodeId}/modules/${moduleId}`,
            req
        );
        return data;
    },

    async uninstallModule(nodeId: string, moduleId: string): Promise<void> {
        await http.delete(`/v1/fleet/nodes/${nodeId}/modules/${moduleId}`);
    },

    // --- Setup ---
    async runSetup(req: SetupRequest): Promise<SetupResponse> {
        const { data } = await http.post<SetupResponse>('/setup', req);
        return data;
    },

    async getSetupStatus(): Promise<SetupStatus> {
        const { data } = await http.get<SetupStatus>('/setup/status');
        return data;
    },

    // --- Mesh Links ---
    async listMeshLinks(): Promise<MeshLink[]> {
        const { data } = await http.get<MeshLink[]>('/v1/fleet/mesh');
        return data;
    },

    async listNodeMeshLinks(nodeId: string): Promise<MeshLink[]> {
        const { data } = await http.get<MeshLink[]>(`/v1/fleet/mesh/nodes/${nodeId}`);
        return data;
    },

    async createMeshLink(req: { fromNodeId: string; toNodeId: string; transportProtocol: string; config?: Record<string, string> }): Promise<MeshLink> {
        const { data } = await http.post<MeshLink>('/v1/fleet/mesh', req);
        return data;
    },

    async applyMeshLink(linkId: string): Promise<void> {
        await http.post(`/v1/fleet/mesh/${linkId}/apply`);
    },

    async deleteMeshLink(linkId: string): Promise<void> {
        await http.delete(`/v1/fleet/mesh/${linkId}`);
    },

    async getMeshStatus(nodeId: string): Promise<unknown> {
        const { data } = await http.get(`/v1/fleet/mesh/nodes/${nodeId}/status`);
        return data;
    }
};

export interface SetupRequest {
    dbHost: string;
    dbName: string;
    dbUser: string;
    dbPassword: string;
    adminUsername: string;
    adminPassword: string;
    nodeName: string;
    nodeRole: string;
}

export interface SetupResponse {
    success: boolean;
    message: string;
}

export interface SetupStatus {
    completed: boolean;
}
